import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { OutlinedBadge } from '@tms/shared-ui';
import { getTeacherDetail, type TeacherDetail } from '../services/adminAnalyticsApi';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TeacherDetailPage: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const { currentLanguage, navigateWithLang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeacherDetail | null>(null);

  const load = useCallback(async () => {
    if (!teacherId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getTeacherDetail(teacherId);
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load teacher');
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <ContentPageLayout>
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
      </ContentPageLayout>
    );
  }

  if (error || !data) {
    return (
      <ContentPageLayout>
      <Box>
        <Alert severity="error">{error || 'Teacher not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigateWithLang('/teachers')}>
          Back to teachers
        </Button>
      </Box>
      </ContentPageLayout>
    );
  }

  const { teacher, stats, workloadTrend, students } = data;
  const displayName =
    `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() ||
    teacher.nickname ||
    teacher.email;

  return (
    <ContentPageLayout>
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigateWithLang('/teachers')}
        sx={{ mb: 2 }}
        size="small"
      >
        Back to teachers
      </Button>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <Typography variant="h5">{displayName}</Typography>
        {stats.overloaded ? (
          <OutlinedBadge label="Overloaded" color="error" size="small" />
        ) : stats.studentCount === 0 ? (
          <OutlinedBadge label="No students" size="small" />
        ) : (
          <OutlinedBadge label="OK" color="success" size="small" />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {teacher.email}
        {[teacher.city, teacher.postalCode].filter(Boolean).length
          ? ` · ${[teacher.city, teacher.postalCode].filter(Boolean).join(' ')}`
          : ''}
        {' · '}
        Last active {formatDate(teacher.lastActivityAt)}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h6">{stats.studentCount}</Typography>
            <Typography variant="caption" color="text.secondary">
              Students
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h6">{stats.activeStudents30d}</Typography>
            <Typography variant="caption" color="text.secondary">
              Active 30d
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h6">{stats.atRiskStudents}</Typography>
            <Typography variant="caption" color="text.secondary">
              Inactive
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h6">{stats.sessionsTotal}</Typography>
            <Typography variant="caption" color="text.secondary">
              Sessions (all time)
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Workload trend (12 months)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Course sessions per month. Assigned student count shown as current roster size.
            </Typography>
            {workloadTrend.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No sessions in the last 12 months.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={workloadTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="sessions" fill="#2e7d32" name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Students ({students.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No students assigned.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((s) => (
                    <TableRow key={s.studentId} hover>
                      <TableCell>
                        <RouterLink to={`/${currentLanguage}/users/${s.studentId}`}>
                          {s.displayName}
                        </RouterLink>
                      </TableCell>
                      <TableCell>
                        {[s.city, s.postalCode].filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      <TableCell>
                        {s.subscriptionStatus
                          ? `${s.subscriptionStatus}${s.isTrial ? ' (trial)' : ''}`
                          : 'none'}
                      </TableCell>
                      <TableCell>{formatDate(s.lastActivityAt)}</TableCell>
                      <TableCell>
                        {s.active30d ? (
                          <OutlinedBadge label="Active" color="success" size="small" />
                        ) : (
                          <OutlinedBadge label="Inactive" color="warning" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              sx={{ mb: 1 }}
              component={RouterLink}
              to={`/${currentLanguage}/users/${teacher.teacherId}`}
            >
              Open user 360° profile
            </Button>
            <Typography variant="body2" color="text.secondary">
              Threshold: {data.workloadThreshold} students per teacher.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
    </ContentPageLayout>
  );
};

export default TeacherDetailPage;
